import type {ReactElement} from 'react';
import {Suspense, useState} from 'react';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import type {ButtonGroupProps, ButtonSpec} from './button-group.types';
import {BUTTON_APPEARANCE_BY_STYLE, MAX_BUTTONS} from './button-group.types';
import {dispatchButtonAction} from './button-group.dispatch';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {useAiChatCallbacks} from '@AiChat/callbacks/ai-chat-callbacks.context';
import {ButtonActionType} from '@Components/ai-chat-window/lib/chat-button-actions.types';
import {LazyTurnIntoVideoButton} from './components/turn-into-video-button/turn-into-video-button.library';
import styles from './button-group.module.scss';

export function ButtonGroup({buttons}: ButtonGroupProps): ReactElement {
  const {state} = useAiChat();
  const callbacks = useAiChatCallbacks();
  const [busyIndex, setBusyIndex] = useState<number | null>(null);

  const isDispatchable = (button: ButtonSpec): boolean => {
    if (button.action.type !== ButtonActionType.USE_THIS) {
      return true;
    }

    return callbacks.onUseAsset !== undefined;
  };

  const visibleButtons = (buttons ?? []).filter(isDispatchable).slice(0, MAX_BUTTONS);

  if (visibleButtons.length === 0) {
    return <></>;
  }

  const handleClick = async (button: ButtonSpec, index: number): Promise<void> => {
    if (busyIndex !== null) {
      return;
    }
    setBusyIndex(index);
    try {
      await dispatchButtonAction(button.action, state.promptBoxId, callbacks);
    } finally {
      setBusyIndex(null);
    }
  };

  const renderButton = (button: ButtonSpec, index: number): ReactElement => {
    const buttonAppearance = button.style ? BUTTON_APPEARANCE_BY_STYLE[button.style] : {type: ButtonType.PRIMARY};

    if (button.action.type === ButtonActionType.TURN_INTO_VIDEO) {
      return (
        <Suspense key={`${button.label}-${index}`} fallback={<Button text={button.label} icon={button.icon} {...buttonAppearance} size={ButtonSize.SMALL} customClasses="radius-8" textClasses="_text-transform-none" disabled />}>
          <LazyTurnIntoVideoButton label={button.label} icon={button.icon} appearance={buttonAppearance} action={button.action} />
        </Suspense>
      );
    }

    return (
      <Button
        key={`${button.label}-${index}`}
        text={button.label}
        icon={button.icon}
        {...buttonAppearance}
        size={ButtonSize.SMALL}
        customClasses="radius-8"
        textClasses="_text-transform-none"
        isLoading={busyIndex === index}
        disabled={busyIndex !== null && busyIndex !== index}
        onClick={(): void => {
          void handleClick(button, index);
        }}
      />
    );
  };

  return (
    <div className={styles.buttonGroup} role="group" aria-label={window.i18next.t('pmwjs_suggested_actions')}>
      {visibleButtons.map(renderButton)}
    </div>
  );
}
